import {VirtualizedFilterList} from "~/components/dataViewer/virtualizedFilterList";
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import SearchIcon from "@atlaskit/icon/core/search";
import {token} from "@atlaskit/tokens";
import {useMediaQuery} from "@mantine/hooks";
import {useQuery} from "@tanstack/react-query";
import {useMemo, useState} from "react";
import {BottomDrawer} from "~/components/BottomDrawer";
import {DebuggingPanel} from "~/components/debuggingPanel";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {
    applyColumnDateRangeToFilters,
    FilterColumnDateRangePill,
    readColumnDateRangeFromFilters,
} from "~/components/ottopilotDataViewer/filterColumnDateRangePill";
import {FilterSelectAllRow} from "~/components/ottopilotDataViewer/filterSelectAllRow";
import {
    buildFilterColumnValuesForPicker,
    buildFilterDisplayLabelIndex,
    countSelectedFilterPickerValues,
    countUniqueSelectedFilterDisplayLabels,
    excludeValuesFromFilterPicker,
    isFilterPickerValueSelected,
    normalizeFilterNullishRawValues,
    resolveSelectAllEphemeralLineItems,
} from "~/components/ottopilotDataViewer/filterValueDisplayGrouping";
import {toggleFilterValuesByDisplayLabel} from "~/components/dataViewer/filterValueSelection";
import {sanitizeCreatedUpdatedByFilterColumnValues} from "~/utilities/deprecatedSystemGeneratedCreatedUpdatedByUserIds";
import {removeInQueueLeadOwnerColumnFiltersExceptAssignment} from "~/utilities/inQueueAssignmentFilters";
import {filterConfigurationExcludingColumn, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {
    clearAllDataViewerFilters,
    hasActiveDataViewerFilters,
} from "~/components/ottopilotDataViewer/dataViewerFilterActions";
import {getDataViewerDateRangeColumnId} from "~/components/ottopilotDataViewer/mobileFilterDrawerHelpers";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {AnankeBadge} from "@vesta/ananke/components/anankeBadge";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeDropdownItemCheckbox} from "@vesta/ananke/components/anankeDropdownItemCheckbox";
import {AnankePopup} from "@vesta/ananke/components/anankePopup";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

/** Minimal contract lets admin viewers use the same filter UI without workspace contexts. */
export type FilterComponentController<T extends {id: Uuid}> = Pick<OttopilotDataViewerController<T>,
    "columns" | "filterConfiguration" | "searchConfiguration" | "visibleFilters" | "setFilterConfiguration" | "setSearchConfiguration" | "setVisibleFilters" | "columnValuesQueryFn" | "queryKeyPrefix" | "dateRangeColumnId" | "filterClearConfig" | "defaultCreatedAtMonthFilter"
>;

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";
export const NULL_SENTINEL: Uuid = "00000000-0000-4000-a000-000000000000" as Uuid;

const FILTER_TRIGGER_BUTTON_STYLE = {
    border: `1px solid ${token("color.border", "#dfe1e6")}`,
    borderRadius: "3px",
    backgroundColor: token("elevation.surface", "#ffffff"),
    color: token("color.text", "#172b4d"),
    paddingInline: token("space.100", "8px"),
    paddingBlock: token("space.050", "4px"),
} as const;

export type VisibleFiltersConfiguration = {
    revisionId: Uuid;
    lineItems: Array<string>;
};

export function FilterVisibleLineItems<
    T extends {
        id: Uuid;
    },
>({controller}: {controller: FilterComponentController<T>}) {
    return (
        <ItemBuilder
            items={controller.visibleFilters.lineItems}
            itemBuilder={(columnId) => {
                // The dedicated date-range control owns this field; do not render a second pill.
                if (columnId === getDataViewerDateRangeColumnId(controller)) {
                    return null;
                }
                const column = controller.columns.lineItems.find((column) => column.id === columnId);
                if (column === undefined) {
                    return null;
                }
                return (
                    <FilterLineItemComponent
                        key={column.id}
                        controller={controller}
                        column={column}
                    />
                );
            }}
        />
    );
}

export function FilterAddFilterButton<
    T extends {
        id: Uuid;
    },
>({controller}: {controller: FilterComponentController<T>}) {
    return <VisibleFiltersComponent controller={controller} />;
}

export function FilterClearAllButton<
    T extends {
        id: Uuid;
    },
>({controller}: {controller: FilterComponentController<T>}) {
    const filterRevisionId = controller.filterConfiguration.revisionId;
    const searchValue = controller.searchConfiguration.searchValue;
    const hasActiveFilters = hasActiveDataViewerFilters(controller);
    void filterRevisionId;
    void searchValue;

    if (hasActiveFilters !== true) {
        return null;
    }

    return (
        <AnankeButton
            type="button"
            appearance="default"
            onClick={() => {
                clearAllDataViewerFilters(controller);
            }}
        >
            Clear
        </AnankeButton>
    );
}

export function FilterComponentStupider<
    T extends {
        id: Uuid;
    },
>({controller}: {controller: FilterComponentController<T>}) {
    return (
        <>
            <FilterVisibleLineItems controller={controller} />
            <FilterAddFilterButton controller={controller} />
        </>
    );
}

export function FilterLineItemComponent<
    T extends {
        id: Uuid;
    },
>({controller, column}: {controller: FilterComponentController<T>; column: OttopilotColumn<T>}) {
    // ── iso8601DateTime filter ──────────────────────────────────────────────
    if (column.type === OttopilotColumnType.iso8601DateTime) {
        const dateRange = readColumnDateRangeFromFilters(controller.filterConfiguration.lineItems, column.id);

        return (
            <FilterColumnDateRangePill
                displayName={column.displayName}
                value={dateRange}
                onChange={(range) => {
                    controller.setFilterConfiguration((prev) =>
                        applyColumnDateRangeToFilters(prev, range, column.id),
                    );
                }}
                matchFilterPills={true}
                shouldRenderToParent={false}
            />
        );
    }

    // ── "in" filter (checkbox list) ─────────────────────────────────────────
    const [ephemeralValues, setEphemeralValues] = useState<{
        revisionId: Uuid;
        lineItems: Array<string> | null;
    }>({
        revisionId: generateUuid(),
        lineItems: null,
    });

    const appliedFilterLineItem = controller.filterConfiguration.lineItems.find(
        (lineItem): lineItem is FilterConfigurationLineItem & {operation: FilterOperation.in; values: Array<string>} =>
            lineItem.columnId === column.id && lineItem.operation === FilterOperation.in,
    );

    const [opened, setOpened] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});

    const openPopup = () => {
        if (appliedFilterLineItem === undefined) {
            setEphemeralValues({
                revisionId: generateUuid(),
                lineItems: null,
            });
        } else {
            setEphemeralValues({
                revisionId: generateUuid(),
                lineItems: normalizeFilterNullishRawValues(
                    appliedFilterLineItem.values ?? [],
                    NULL_SENTINEL,
                ),
            });
        }
        setSearchValue("");
        setOpened(true);
    };
    const closePopup = () => {
        setSearchValue("");
        setOpened(false);
    };
    const closePopupAndDiscardChanges = () => {
        setSearchValue("");
        setOpened(false);
    };

    const filterConfigurationForColumnValues = filterConfigurationExcludingColumn(
        controller.filterConfiguration,
        column.id,
    );

    const columnValuesQuery = useQuery<Array<string>>({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "columns",
            column.id,
            "values",
            filterConfigurationForColumnValues.revisionId,
            JSON.stringify(filterConfigurationForColumnValues.lineItems),
            controller.searchConfiguration.revisionId,
        ],
        queryFn:
            column.filteringCapability_in?.valuesQueryFn !== undefined
                ? () =>
                      column.filteringCapability_in!.valuesQueryFn!({
                          filterConfiguration: filterConfigurationForColumnValues,
                      })
                : () =>
                      controller.columnValuesQueryFn({
                          columnId: column.id,
                          filterConfiguration: filterConfigurationForColumnValues,
                          searchConfiguration: controller.searchConfiguration,
                      }),
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: "always",
        enabled: opened === true,
    });

    const columnValuesFromQuery =
        columnValuesQuery.data !== undefined
            ? sanitizeCreatedUpdatedByFilterColumnValues(column.id, columnValuesQuery.data)
            : undefined;
    const excludePickerValues = column.filteringCapability_in?.excludePickerValues;
    const {columnValuesForPicker, allColumnRawValuesForPicker} = useMemo(() => {
        if (columnValuesFromQuery === undefined) {
            return {
                columnValuesForPicker: [] as Array<string>,
                allColumnRawValuesForPicker: [] as Array<string>,
            };
        }
        const built = buildFilterColumnValuesForPicker({
            queryValues: columnValuesFromQuery,
            appliedFilterValues: appliedFilterLineItem?.values,
            nullSentinel: NULL_SENTINEL,
            valueFormatter:
                column.valueFormatter !== undefined
                    ? (value) => String(column.valueFormatter!(value as never))
                    : undefined,
        });
        return {
            columnValuesForPicker: excludeValuesFromFilterPicker(built.pickerValues, excludePickerValues),
            allColumnRawValuesForPicker: excludeValuesFromFilterPicker(built.allRawValues, excludePickerValues),
        };
    }, [
        columnValuesFromQuery,
        appliedFilterLineItem?.values,
        excludePickerValues,
        column.valueFormatter,
    ]);
    const filterValueFormatter =
        column.valueFormatter !== undefined
            ? (value: string) => String(column.valueFormatter!(value as never))
            : undefined;
    const ephemeralSelectedSet = useMemo(() => {
        if (ephemeralValues.lineItems === null) {
            return null;
        }
        return new Set(ephemeralValues.lineItems);
    }, [ephemeralValues.lineItems]);
    const displayLabelIndex = useMemo(() => {
        if (filterValueFormatter === undefined) {
            return null;
        }
        return buildFilterDisplayLabelIndex(allColumnRawValuesForPicker, filterValueFormatter);
    }, [filterValueFormatter, allColumnRawValuesForPicker]);
    const isPickerValueSelected = (columnValue: string): boolean => {
        return isFilterPickerValueSelected({
            columnValue: columnValue,
            selectedValues: ephemeralValues.lineItems,
            selectedValuesSet: ephemeralSelectedSet,
            valueFormatter: filterValueFormatter,
            displayLabelIndex: displayLabelIndex,
            nullSentinel: NULL_SENTINEL,
        });
    };
    const appliedFilterValuesForBadge =
        appliedFilterLineItem?.values !== undefined
            ? excludeValuesFromFilterPicker(
                  normalizeFilterNullishRawValues(appliedFilterLineItem.values, NULL_SENTINEL),
                  excludePickerValues,
              )
            : undefined;
    const appliedFilterBadgeCount =
        appliedFilterValuesForBadge !== undefined
            ? filterValueFormatter === undefined
                ? appliedFilterValuesForBadge.length
                : countUniqueSelectedFilterDisplayLabels(
                      appliedFilterValuesForBadge,
                      filterValueFormatter,
                      NULL_SENTINEL,
                  )
            : 0;

    const filteredColumnValues = useMemo(() => {
        if (columnValuesForPicker.length === 0) {
            return [];
        }
        if (searchValue.trim() === "") {
            return columnValuesForPicker;
        }
        const searchLower = searchValue.toLowerCase().trim();
        return columnValuesForPicker.filter((columnValue) => {
            if (columnValue === NULL_SENTINEL) {
                return "null".includes(searchLower);
            }
            const displayValue =
                column.valueFormatter !== undefined
                    ? String(column.valueFormatter(columnValue as never))
                    : String(columnValue);
            return displayValue.toLowerCase().includes(searchLower);
        });
    }, [columnValuesForPicker, searchValue, column.valueFormatter]);

    const selectedFilteredPickerCount = useMemo(() => {
        return countSelectedFilterPickerValues({
            pickerValues: filteredColumnValues,
            selectedValues: ephemeralValues.lineItems,
            selectedValuesSet: ephemeralSelectedSet,
            valueFormatter: filterValueFormatter,
            displayLabelIndex: displayLabelIndex,
            nullSentinel: NULL_SENTINEL,
        });
    }, [
        filteredColumnValues,
        ephemeralValues.lineItems,
        ephemeralSelectedSet,
        filterValueFormatter,
        displayLabelIndex,
    ]);

    const handleSelectAllChange = (checked: boolean) => {
        if (checked === false) {
            setEphemeralValues({
                revisionId: generateUuid(),
                lineItems: [],
            });
            return;
        }
        setEphemeralValues((prev) => ({
            revisionId: generateUuid(),
            lineItems: resolveSelectAllEphemeralLineItems({
                searchQuery: searchValue,
                filteredPickerValues: filteredColumnValues,
                allRawValues: allColumnRawValuesForPicker,
                previousLineItems: prev.lineItems,
                valueFormatter: filterValueFormatter,
                nullSentinel: NULL_SENTINEL,
            }),
        }));
    };

    const renderColumnValue = (columnValue: string): React.ReactNode => {
        if (columnValue === NULL_SENTINEL || columnValue.trim() === "" || columnValue === "-") {
            return "NULL";
        }
        if (column.valueFormatter !== undefined) {
            const formatted = column.valueFormatter(columnValue as never);
            if (formatted === null || formatted === undefined) {
                return "NULL";
            }
            const formattedText = String(formatted);
            if (formattedText.trim() === "" || formattedText === "-") {
                return "NULL";
            }
            return formatted;
        }
        return columnValue;
    };

    const handleApplyInFilter = () => {
        if (column.customFilter !== undefined) {
            void Promise.resolve(
                column.customFilter({
                    controller: controller,
                    ephemeralValue: ephemeralValues,
                    column: column,
                    closePopup: closePopup,
                }),
            );
            return;
        }
        if (ephemeralValues.lineItems === null) {
            controller.setFilterConfiguration((prev) => ({
                revisionId: generateUuid(),
                lineItems: removeInQueueLeadOwnerColumnFiltersExceptAssignment(
                    prev.lineItems,
                    column.id,
                ),
            }));
        } else {
            controller.setFilterConfiguration((prev) => ({
                revisionId: generateUuid(),
                lineItems: [
                    ...removeInQueueLeadOwnerColumnFiltersExceptAssignment(prev.lineItems, column.id),
                    {
                        columnId: column.id,
                        operation: FilterOperation.in,
                        values: normalizeFilterNullishRawValues(
                            ephemeralValues.lineItems!,
                            NULL_SENTINEL,
                        ),
                    },
                ],
            }));
        }
        closePopup();
    };

    const onItemClick = (columnValue: string) => {
        setEphemeralValues((prev) => {
            if (filterValueFormatter === undefined) {
                if (prev.lineItems === null) {
                    return {
                        revisionId: generateUuid(),
                        lineItems: columnValuesForPicker.filter((value) => value !== columnValue),
                    };
                }
                const wasPreviouslySelected = prev.lineItems.includes(columnValue);
                if (wasPreviouslySelected) {
                    return {
                        revisionId: generateUuid(),
                        lineItems: prev.lineItems.filter((lineItem) => lineItem !== columnValue),
                    };
                }
                return {
                    revisionId: generateUuid(),
                    lineItems: [...prev.lineItems, columnValue],
                };
            }
            return {
                revisionId: generateUuid(),
                lineItems: toggleFilterValuesByDisplayLabel(
                    columnValue,
                    prev.lineItems,
                    allColumnRawValuesForPicker,
                    filterValueFormatter,
                    NULL_SENTINEL,
                ),
            };
        });
    };

    const searchField = (
        <div className="px-ds-space-075 pt-ds-space-100">
            <AnankeTextField
                id={`filter-search-${column.id}`}
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchValue(e.currentTarget.value);
                }}
                placeholder="Search"
                elemBeforeInput={
                    <div className="pl-ds-space-100 grid place-items-center">
                        <SearchIcon label="" />
                    </div>
                }
            />
        </div>
    );

    if (isMobile === true) {
        return (
            <>
                <AnankeButton
                    type="button"
                    appearance="default"
                    style={FILTER_TRIGGER_BUTTON_STYLE}
                    isSelected={appliedFilterLineItem !== undefined}
                    onClick={() => {
                        if (opened === false) {
                            openPopup();
                        } else {
                            closePopup();
                        }
                    }}
                >
                    <div className="grid grid-flow-col auto-cols-max gap-x-[0.375rem] items-center">
                        {column.displayName}
                        {appliedFilterLineItem !== undefined ? (
                            <AnankeBadge appearance="primary">{appliedFilterBadgeCount}</AnankeBadge>
                        ) : null}
                    </div>
                </AnankeButton>

                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title={column.displayName}
                    zIndex={100}
                    fitContentOnOpen={false}
                    initialOpenHeight={480}
                    footer={
                        <div>
                            <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
                            <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                                <AnankeButton
                                    type="button"
                                    appearance="subtle"
                                    onClick={closePopupAndDiscardChanges}
                                >
                                    Cancel
                                </AnankeButton>
                                <AnankeButton
                                    type="button"
                                    appearance="primary"
                                    onClick={handleApplyInFilter}
                                    isDisabled={
                                        controller.visibleFilters.revisionId === ephemeralValues.revisionId
                                    }
                                >
                                    Apply
                                </AnankeButton>
                            </div>
                        </div>
                    }
                >
                    {searchField}
                    {columnValuesQuery.status === "pending" ? (
                        <div className="px-ds-space-200 py-ds-space-100">Loading</div>
                    ) : columnValuesQuery.status === "error" ? (
                        <div className="px-ds-space-200 py-ds-space-100">
                            Error: {columnValuesQuery.error.message}
                        </div>
                    ) : filteredColumnValues.length === 0 ? (
                        <div className="px-ds-space-200 py-ds-space-100 text-ds-text-subtlest">
                            No matching values found
                        </div>
                    ) : (
                        <>
                            <FilterSelectAllRow
                                isChecked={
                                    ephemeralValues.lineItems === null
                                        ? true
                                        : ephemeralValues.lineItems.length > 0
                                }
                                isIndeterminate={
                                    ephemeralValues.lineItems === null
                                        ? false
                                        : selectedFilteredPickerCount > 0 &&
                                        selectedFilteredPickerCount < filteredColumnValues.length
                                }
                                onChange={(e) => {
                                    handleSelectAllChange(e.target.checked);
                                }}
                            />

                            <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />

                            <div className="min-h-0 flex-1">
                                <VirtualizedFilterList
                                    items={filteredColumnValues}
                                    isSelected={isPickerValueSelected}
                                    onItemClick={onItemClick}
                                    renderItem={(columnValue) => renderColumnValue(columnValue)}
                                />
                            </div>
                        </>
                    )}
                </BottomDrawer>

                <DebuggingPanel className="bottom-[10rem] max-w-[19rem] max-h-[9rem] overflow-auto">
                    <div className="font-bold">OttopilotDataViewer: visibleFilters</div>
                    <div className="whitespace-pre">{JSON.stringify(ephemeralValues, null, 4)}</div>
                </DebuggingPanel>
            </>
        );
    }

    // ── Desktop: original AnankePopup implementation ────────────────────────
    return (
        <>
            <AnankePopup
                isOpen={opened}
                onClose={() => setOpened(false)}
                trigger={(triggerProps) => (
                    <AnankeButton
                        type="button"
                        appearance="default"
                        style={FILTER_TRIGGER_BUTTON_STYLE}
                        {...triggerProps}
                        isSelected={appliedFilterLineItem !== undefined}
                        onClick={() => {
                            if (opened === false) {
                                openPopup();
                            } else {
                                closePopup();
                            }
                        }}
                    >
                        <div className="grid grid-flow-col auto-cols-max gap-x-[0.375rem] items-center">
                            {column.displayName}
                            {appliedFilterLineItem !== undefined ? (
                                <AnankeBadge appearance="primary">{appliedFilterBadgeCount}</AnankeBadge>
                            ) : null}
                        </div>
                    </AnankeButton>
                )}
                content={() => {
                    return (
                        <div className="w-full min-w-[20rem] max-w-[20rem] h-[20rem] grid grid-cols-1 grid-rows-[max-content_max-content_max-content_max-content_minmax(0,1fr)_max-content_max-content]">
                            {searchField}
                            {columnValuesQuery.status === "pending" ? (
                                <div className="w-full h-full grid grid-cols-1 grid-rows-1 px-ds-space-200 py-ds-space-100">
                                    Loading
                                </div>
                            ) : columnValuesQuery.status === "error" ? (
                                <div className="w-full h-full grid grid-cols-1 grid-rows-1 px-ds-space-200 py-ds-space-100">
                                    Error: {columnValuesQuery.error.message}
                                </div>
                            ) : filteredColumnValues.length === 0 ? (
                                <div className="w-full h-full grid grid-cols-1 grid-rows-1 place-items-center px-ds-space-200 py-ds-space-100 text-ds-text-subtlest">
                                    No matching values found
                                </div>
                            ) : (
                                <>
                                    <div />

                                    <FilterSelectAllRow
                                        isChecked={
                                            ephemeralValues.lineItems === null
                                                ? true
                                                : ephemeralValues.lineItems.length > 0
                                        }
                                        isIndeterminate={
                                            ephemeralValues.lineItems === null
                                                ? false
                                                : selectedFilteredPickerCount > 0 &&
                                                selectedFilteredPickerCount < filteredColumnValues.length
                                        }
                                        onChange={(e) => {
                                            handleSelectAllChange(e.target.checked);
                                        }}
                                    />

                                    <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />

                                    <VirtualizedFilterList
                                        items={filteredColumnValues}
                                        isSelected={isPickerValueSelected}
                                        onItemClick={onItemClick}
                                        renderItem={(columnValue) => renderColumnValue(columnValue)}
                                    />

                                    <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />

                                    <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                                        <AnankeButton
                                            type="button"
                                            appearance="subtle"
                                            onClick={closePopupAndDiscardChanges}
                                        >
                                            Cancel
                                        </AnankeButton>

                                        <AnankeButton
                                            type="button"
                                            appearance="primary"
                                            onClick={handleApplyInFilter}
                                            isDisabled={
                                                controller.visibleFilters.revisionId ===
                                                ephemeralValues.revisionId
                                            }
                                        >
                                            Apply
                                        </AnankeButton>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                }}
                placement="bottom-start"
            />

            <DebuggingPanel className="bottom-[10rem] max-w-[19rem] max-h-[9rem] overflow-auto">
                <div className="font-bold">OttopilotDataViewer: visibleFilters</div>
                <div className="whitespace-pre">{JSON.stringify(ephemeralValues, null, 4)}</div>
            </DebuggingPanel>
        </>
    );
}

function VisibleFiltersComponent<
    T extends {
        id: Uuid;
    },
>({controller}: {controller: FilterComponentController<T>}) {
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});

    const [ephemeralVisibleFilters, setEphemeralVisibleFilters] = useState<VisibleFiltersConfiguration>({
        revisionId: generateUuid(),
        lineItems: [],
    });

    const [opened, setOpened] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const openPopup = () => {
        setEphemeralVisibleFilters(controller.visibleFilters);
        setSearchQuery("");
        setOpened(true);
    };
    const closePopup = () => {
        setOpened(false);
    };
    const closePopupAndDiscardChanges = () => {
        setOpened(false);
    };

    const allFilterableColumnIds = controller.columns.lineItems
        .filter(
            (column) =>
                column.id !== getDataViewerDateRangeColumnId(controller) &&
                (column.filteringCapability_contains === true || column.filteringCapability_in !== undefined),
        )
        .map((column) => column.id);
    const nColumnsWithFilteringCapability = allFilterableColumnIds.length;
    const selectedFilterableCount = ephemeralVisibleFilters.lineItems.filter((columnId) =>
        allFilterableColumnIds.includes(columnId),
    ).length;

    const triggerButton = (onClick: () => void) => (
        <AnankeButton
            type="button"
            appearance="default"
            style={FILTER_TRIGGER_BUTTON_STYLE}
            isSelected={opened}
            onClick={onClick}
        >
            + Add Filter
        </AnankeButton>
    );

    const selectAllCheckbox = (
        <FilterSelectAllRow
            isChecked={
                nColumnsWithFilteringCapability > 0
                && selectedFilterableCount === nColumnsWithFilteringCapability
            }
            isIndeterminate={
                selectedFilterableCount > 0 && selectedFilterableCount < nColumnsWithFilteringCapability
            }
            onChange={(e) => {
                const value = e.target.checked;
                if (value === true) {
                    setEphemeralVisibleFilters((prev) => {
                        const nonFilterableVisibleColumnIds = prev.lineItems.filter(
                            (columnId) => allFilterableColumnIds.includes(columnId) === false,
                        );
                        return {
                            revisionId: generateUuid(),
                            lineItems: [...nonFilterableVisibleColumnIds, ...allFilterableColumnIds],
                        };
                    });
                } else {
                    setEphemeralVisibleFilters((prev) => ({
                        revisionId: generateUuid(),
                        lineItems: prev.lineItems.filter(
                            (columnId) => allFilterableColumnIds.includes(columnId) === false,
                        ),
                    }));
                }
            }}
        />
    );

    const filterableColumns = controller.columns.lineItems.filter(
        (column) =>
            column.id !== getDataViewerDateRangeColumnId(controller) &&
            (column.filteringCapability_contains === true || column.filteringCapability_in !== undefined),
    );

    const filteredColumns =
        searchQuery.trim() === ""
            ? filterableColumns
            : filterableColumns.filter((column) =>
                  column.displayName.toLowerCase().includes(searchQuery.toLowerCase()),
              );

    const columnList = (
        <ItemBuilder
            items={filteredColumns}
            itemBuilder={(column) => (
                <AnankeDropdownItemCheckbox
                    key={column.id}
                    id={column.id}
                    isSelected={ephemeralVisibleFilters.lineItems.includes(column.id)}
                    onClick={() => {
                        setEphemeralVisibleFilters((prev) => {
                            const wasPreviouslySelected = prev.lineItems.includes(column.id);
                            if (wasPreviouslySelected) {
                                return {
                                    revisionId: generateUuid(),
                                    lineItems: prev.lineItems.filter((lineItem) => lineItem !== column.id),
                                };
                            } else {
                                return {
                                    revisionId: generateUuid(),
                                    lineItems: [...prev.lineItems, column.id],
                                };
                            }
                        });
                    }}
                >
                    {column.displayName}
                </AnankeDropdownItemCheckbox>
            )}
        />
    );

    const actionButtons = (onCancel: () => void) => (
        <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
            <AnankeButton
                type="button"
                appearance="subtle"
                onClick={onCancel}
            >
                Cancel
            </AnankeButton>

            <AnankeButton
                type="button"
                appearance="primary"
                onClick={() => {
                    controller.setVisibleFilters(ephemeralVisibleFilters);
                    closePopup();
                }}
                isDisabled={controller.visibleFilters.revisionId === ephemeralVisibleFilters.revisionId}
            >
                Apply
            </AnankeButton>
        </div>
    );

    if (isMobile === true) {
        return (
            <>
                {triggerButton(() => {
                    if (opened === false) {
                        openPopup();
                    } else {
                        closePopup();
                    }
                })}
                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title="Add Filter"
                    zIndex={100}
                    fitContentOnOpen={false}
                    initialOpenHeight={400}
                    footer={
                        <div>
                            <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
                            {actionButtons(closePopupAndDiscardChanges)}
                        </div>
                    }
                >
                    <div className="px-ds-space-100 pt-ds-space-100 pb-ds-space-050">
                        <AnankeTextField
                            placeholder="Search filters..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery((e.target as HTMLInputElement).value);
                            }}
                            elemBeforeInput={
                                <div className="pl-ds-space-100 grid place-items-center">
                                    <SearchIcon label="" />
                                </div>
                            }
                        />
                    </div>
                    {selectAllCheckbox}
                    <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
                    {columnList}
                </BottomDrawer>
            </>
        );
    }

    // ── Desktop: original AnankePopup implementation ────────────────────────
    return (
        <AnankePopup
            isOpen={opened}
            onClose={() => setOpened(false)}
            trigger={(triggerProps) => (
                <AnankeButton
                    type="button"
                    appearance="default"
                    style={FILTER_TRIGGER_BUTTON_STYLE}
                    {...triggerProps}
                    isSelected={opened}
                    onClick={() => {
                        if (opened === false) {
                            openPopup();
                        } else {
                            closePopup();
                        }
                    }}
                >
                    + Add Filter
                </AnankeButton>
            )}
            content={() => (
                <div className="w-full max-w-[20rem] h-[20rem] grid grid-cols-1 grid-rows-[max-content_max-content_max-content_minmax(0,1fr)_max-content_max-content]">
                    <div className="px-ds-space-100 pt-ds-space-100">
                        <AnankeTextField
                            placeholder="Search filters..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery((e.target as HTMLInputElement).value);
                            }}
                            elemBeforeInput={
                                <div className="pl-ds-space-100 grid place-items-center">
                                    <SearchIcon label="" />
                                </div>
                            }
                        />
                    </div>
                    {selectAllCheckbox}
                    <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
                    <div className="w-full h-full overflow-auto">
                        {columnList}
                    </div>
                    <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
                    {actionButtons(closePopupAndDiscardChanges)}
                </div>
            )}
            placement="bottom-end"
        />
    );
}
